<script setup lang="ts">
type SimpleItem = { title: string; body: string }
type FaqItem = { q: string; a: string }

const supermarketCategories: SimpleItem[] = [
    {
        title: "Alimentos y productos básicos",
        body: "Arroz, aceite, azúcar, café, carnes, lácteos, pan, conservas, bebidas, productos de limpieza y artículos de aseo personal. Para muchas familias, Dapamas funciona como el supermercado online Cuba donde resuelven varias necesidades en un solo pedido.",
    },
    {
        title: "Combos para familias",
        body: "Los combos son la opción más práctica para quienes hacen compras para Cuba online de forma regular. Incluyen varios productos esenciales en un único paquete y ayudan a ahorrar tiempo y dinero. Son ideales para cubrir las necesidades de una familia durante semanas sin tener que hacer múltiples pedidos.",
    },
    {
        title: "Paneles solares para Cuba",
        body: "La energía es una necesidad básica. Por eso Dapamas tiene una categoría completa de paneles solares para diferentes necesidades y presupuestos. Puedes comprarlos desde el exterior o directamente desde Cuba y coordinar la entrega igual que con cualquier otro producto.",
    },
    {
        title: "Equipamiento de oficina",
        body: "Para quien trabaja desde casa o tiene un negocio en la isla, Dapamas ofrece una selección de equipamiento de oficina: escritorios, monitores, ventiladores, accesorios y más. Todo lo necesario para trabajar cómodo, disponible para comprar y recibir en La Habana y Villa Clara.",
    },
]

const paymentMethods: SimpleItem[] = [
    {
        title: "Tarjeta de crédito o débito",
        body: "Visa, Mastercard, American Express y Discover. Disponible desde cualquier país.",
    },
    {
        title: "Criptomonedas",
        body: "Para todo el mundo. En Cuba se puede usar la billetera Ebioro: fácil de descargar y utilizar.",
    },
    {
        title: "Transferencia bancaria",
        body: "Disponible para clientes en México.",
    },
    {
        title: "Efectivo en Oxxo",
        body: "Para México. Tiendas de conveniencia abiertas 24 horas en todo el país.",
    },
]

const whyChooseUs: SimpleItem[] = [
    {
        title: "Productos disponibles de verdad",
        body: "Trabajamos con proveedores locales verificados dentro de Cuba. Lo que ves en el catálogo es lo que hay en stock, no lo que debería haber.",
    },
    {
        title: "El mejor precio",
        body: "Nos esforzamos por ofrecer los precios más competitivos en todos nuestros productos, sin sacrificar calidad ni servicio.",
    },
    {
        title: "Diferentes métodos de pago",
        body: "Visa, Mastercard, American Express, Discover, criptomonedas, transferencias bancarias en México y pagos en Oxxo.",
    },
    {
        title: "Precios claros",
        body: "Lo que ves es lo que pagas. Si eliges entrega a domicilio, el coste aparece antes de confirmar, sin cargos ocultos ni sorpresas.",
    },
    {
        title: "Seguimiento del pedido",
        body: "Puedes revisar el estado de tu compra en cualquier momento, desde que se confirma el pago hasta que llega a manos de tu familia.",
    },
    {
        title: "Atención en español",
        body: "Equipo disponible por WhatsApp, correo y redes sociales, con conocimiento real del contexto cubano y políticas claras de reposición y reembolso.",
    },
]

const shoppingSteps: string[] = [
    "Elige la provincia de entrega: La Habana o Villa Clara.",
    "Explora el catálogo o busca los productos que necesitas.",
    "Añade artículos al carrito y ajusta cantidades.",
    "Elige cómo pagar: tarjeta, cripto, transferencia o efectivo en Oxxo (México).",
    "Confirma el pedido y sigue su estado en tiempo real.",
    "Tu familia recoge en la tienda del proveedor o lo recibe en casa.",
]

const faqs: FaqItem[] = [
    {
        q: "¿Cuánto tarda un pedido en Cuba?",
        a: "La mayoría están listos entre 24 y 72 horas hábiles después de confirmar el pago. Recibirás una notificación con el estado actualizado de tu compra online para Cuba en todo momento.",
    },
    {
        q: "¿Desde qué países puedo comprar?",
        a: "Desde prácticamente cualquier lugar del mundo: España, Estados Unidos, México, Italia, Canadá, Francia, Países Bajos y más. Si tienes tarjeta internacional, criptomonedas o acceso a Oxxo en México, puedes hacer tus compras para Cuba online sin problema.",
    },
    {
        q: "¿Se puede comprar desde Cuba?",
        a: "Sí. Quienes están en la isla pueden usar la billetera de cripto Ebioro: alguien del exterior carga el saldo y tú compras directamente desde Cuba de forma independiente.",
    },
    {
        q: "¿Es seguro comprar en Dapamas?",
        a: "Sí. Usamos pasarelas de pago certificadas con cifrado de extremo a extremo. Tus datos de tarjeta no se almacenan en nuestros servidores. Comprar en Dapamas es igual de seguro que hacerlo en cualquier tienda internacional de confianza.",
    },
    {
        q: "¿Qué pasa si hay un problema con el pedido?",
        a: "Nuestro equipo de soporte puede ayudarte con incidencias, reposiciones o reembolsos. Contamos con políticas claras y un equipo que conoce el contexto cubano.",
    },
    {
        q: "¿Dapamas es solo para enviar desde fuera o también funciona dentro de Cuba?",
        a: "Las dos cosas. Opera como plataforma de envíos a Cuba online para la diáspora en el exterior, y como tienda online en Cuba para quienes viven en la isla. El mismo catálogo y las mismas garantías para los dos.",
    },
]
</script>

<template>
    <section
        class="seo-section tw-bg-white tw-w-full tw-py-12 md:tw-py-16 max-container tw-mx-auto"
        aria-labelledby="seo-section-title"
    >
        <div class="tw-px-4 md:tw-px-0">
            <header class="tw-flex tw-flex-col tw-gap-4 tw-mb-8 md:tw-mb-12">
                <h1
                    id="seo-section-title"
                    class="tw-font-titilium tw-font-bold tw-text-primary tw-text-2xl md:tw-text-[32px] tw-leading-tight"
                >
                    Tienda online para envíos a Cuba fáciles y seguros
                </h1>
                <p
                    class="tw-font-titilium tw-text-customGray-600 tw-text-base md:tw-text-lg tw-leading-relaxed"
                >
                    Dapamas reúne los mejores productos disponibles en Cuba y es
                    la mejor tienda online para envíos a Cuba. En un solo lugar
                    puedes comprar alimentos, combos, productos del hogar,
                    paneles solares y equipamiento de oficina desde cualquier
                    país. Pagos con tarjeta de débito o crédito internacional,
                    criptomonedas o efectivo (solo México), y tu familia recoge
                    el pedido cerca de casa o lo enviamos hasta su puerta. Con
                    los mejores precios de todas las tiendas online para envíos
                    a Cuba.
                </p>
            </header>

            <div class="seo-accordion tw-flex tw-flex-col tw-gap-3">
                <!-- Tu tienda online en Cuba y para Cuba -->
                <details class="seo-item">
                    <summary class="seo-item__header">
                        <h2 class="seo-item__title">
                                Tu tienda online en Cuba y para Cuba
                            </h2>
                    </summary>
                    <div class="seo-item__content">
                        <p>
                            Dapamas es una plataforma pensada para quienes
                            necesitan hacer
                            <strong>compras online para Cuba</strong> o enviar
                            productos a sus familiares desde el exterior. Reúne
                            proveedores locales verificados en un solo lugar
                            para que encuentres lo que necesitas sin buscar en
                            sitios distintos, sin intermediarios y a un precio
                            mejor que si los envías físicamente.
                        </p>
                        <p>
                            Da igual si vives en España, Estados Unidos, México,
                            Italia o en cualquier otro país: compras desde tu
                            teléfono o computadora y tu familia recibe el pedido
                            en Cuba. Y si estás en la isla, Dapamas también
                            funciona como una
                            <strong>tienda online en Cuba</strong>, aunque el
                            pago desde Cuba se hace con criptomonedas mediante
                            el monedero electrónico de Ebioro.
                        </p>
                    </div>
                </details>

                <!-- ¿Qué es Dapamas? -->
                <details class="seo-item">
                    <summary class="seo-item__header">
                        <h2 class="seo-item__title">¿Qué es Dapamas?</h2>
                    </summary>
                    <div class="seo-item__content">
                        <p>
                            Dapamas funciona como un marketplace digital para
                            Cuba. Conecta a personas del exterior con
                            proveedores locales dentro de la isla para que las
                            compras sean reales, los precios bajos y
                            transparentes, y las entregas posibles: puedes
                            elegir tiendas cerca de la casa donde se va a
                            entregar.
                        </p>
                        <p>
                            Entre las
                            <strong>tiendas online en Cuba</strong> disponibles
                            hoy, Dapamas destaca por trabajar directamente con
                            negocios de la comunidad: tiendas que conocen el
                            terreno, tienen stock real y entregan sin las
                            demoras de los modelos de almacén centralizado.
                        </p>
                    </div>
                </details>

                <!-- Compras para Cuba online desde cualquier lugar -->
                <details class="seo-item">
                    <summary class="seo-item__header">
                        <h2 class="seo-item__title">
                                Compras para Cuba online desde cualquier lugar
                            </h2>
                    </summary>
                    <div class="seo-item__content">
                        <p>
                            Muchas familias separadas por la distancia buscan
                            una forma confiable de enviar lo que sus seres
                            queridos necesitan. Con Dapamas, hacer
                            <strong>compras online para Cuba</strong> es tan
                            sencillo como comprar en cualquier tienda moderna:
                        </p>
                        <ol class="seo-list seo-list--steps">
                            <li
                                v-for="(step, i) in shoppingSteps"
                                :key="`step-${i}`"
                            >
                                <span class="seo-list__index">{{ i + 1 }}</span>
                                <span>{{ step }}</span>
                            </li>
                        </ol>
                        <p>
                            ¿La diferencia con otras plataformas? Primero el
                            precio: nos esforzamos por ofrecer los más
                            competitivos en todos nuestros productos. Además,
                            los proveedores de Dapamas están dentro de Cuba,
                            cerca de donde vive tu familia. Menos esperas, más
                            disponibilidad y un servicio que realmente conoce
                            Cuba.
                        </p>
                    </div>
                </details>

                <!-- Compra online en Cuba sin complicaciones -->
                <details class="seo-item">
                    <summary class="seo-item__header">
                        <h2 class="seo-item__title">
                                Compra online en Cuba sin complicaciones
                            </h2>
                    </summary>
                    <div class="seo-item__content">
                        <p>
                            Dapamas no está pensada solo para quienes viven
                            fuera. Las personas dentro de la isla también pueden
                            usar la plataforma como su
                            <strong>tienda online en Cuba</strong> para hacer
                            sus propias compras, acceder a productos esenciales
                            y pagar de forma segura, aunque los medios de pago
                            deben ser en moneda dura: tarjetas de crédito o
                            débito extranjeras, o la billetera de Ebioro
                            disponible en Cuba.
                        </p>
                        <p>
                            Entre las
                            <strong>tiendas virtuales en Cuba</strong> con
                            productos variados, precios accesibles y
                            transparentes y un proceso sencillo, Dapamas ofrece
                            una experiencia clara: exploras, eliges, pagas y
                            recoges.
                        </p>
                        <p>
                            Si estás en Cuba, puedes usar la billetera de
                            criptomonedas <strong>Ebioro</strong>.
                        </p>
                    </div>
                </details>

                <!-- ¿Cómo recibe el pedido tu familia? -->
                <details class="seo-item">
                    <summary class="seo-item__header">
                        <h2 class="seo-item__title">
                                ¿Cómo recibe el pedido tu familia?
                            </h2>
                    </summary>
                    <div class="seo-item__content">
                        <p>
                            Hacer <strong>envíos para Cuba online</strong> con
                            Dapamas es muy fácil. Una vez confirmada la compra,
                            el pedido puede recibirse de dos maneras:
                        </p>
                        <div class="seo-cards seo-cards--two">
                            <article class="seo-card">
                                <h3 class="seo-card__title">
                                    Recoger en tienda
                                </h3>
                                <p>
                                    La persona recoge directamente en la tienda
                                    del proveedor, dentro de su propia
                                    comunidad. Sin esperas largas ni
                                    desplazamientos innecesarios. La opción más
                                    rápida y popular.
                                </p>
                            </article>
                            <article class="seo-card">
                                <h3 class="seo-card__title">
                                    Entrega a domicilio
                                </h3>
                                <p>
                                    El pedido llega a la puerta de casa. El
                                    coste depende de cada proveedor y se muestra
                                    antes de confirmar, sin sorpresas. En ambos
                                    casos recibirás confirmación y seguimiento.
                                </p>
                            </article>
                        </div>
                    </div>
                </details>

                <!-- Supermercado online para Cuba y mucho más -->
                <details class="seo-item">
                    <summary class="seo-item__header">
                        <h2 class="seo-item__title">
                                Supermercado online para Cuba y mucho más
                            </h2>
                    </summary>
                    <div class="seo-item__content">
                        <p>
                            El catálogo de Dapamas está pensado para cubrir lo
                            que una familia cubana necesita de verdad: desde la
                            despensa hasta la energía del hogar.
                        </p>
                        <div class="seo-cards seo-cards--two">
                            <article
                                v-for="(item, i) in supermarketCategories"
                                :key="`cat-${i}`"
                                class="seo-card"
                            >
                                <h3 class="seo-card__title">
                                    {{ item.title }}
                                </h3>
                                <p>{{ item.body }}</p>
                            </article>
                        </div>
                    </div>
                </details>

                <!-- ¿Cómo puedo pagar? -->
                <details class="seo-item">
                    <summary class="seo-item__header">
                        <h2 class="seo-item__title">¿Cómo puedo pagar?</h2>
                    </summary>
                    <div class="seo-item__content">
                        <p>
                            Dapamas acepta varios métodos para que puedas
                            comprar sin importar desde dónde lo hagas:
                        </p>
                        <div class="seo-cards seo-cards--two">
                            <article
                                v-for="(item, i) in paymentMethods"
                                :key="`pay-${i}`"
                                class="seo-card"
                            >
                                <h3 class="seo-card__title">
                                    {{ item.title }}
                                </h3>
                                <p>{{ item.body }}</p>
                            </article>
                        </div>
                    </div>
                </details>

                <!-- Provincias disponibles -->
                <details class="seo-item">
                    <summary class="seo-item__header">
                        <h2 class="seo-item__title">
                                Provincias disponibles
                            </h2>
                    </summary>
                    <div class="seo-item__content">
                        <p>
                            Actualmente Dapamas opera en
                            <strong>La Habana y Villa Clara</strong>, con
                            proveedores locales que permiten entregas ágiles y
                            disponibilidad real. Seguimos creciendo hacia otras
                            provincias de forma progresiva.
                        </p>
                        <p>
                            Si tu familia vive en otra provincia, suscríbete a
                            nuestro boletín y te avisamos cuando activemos los
                            envíos en tu área.
                        </p>
                    </div>
                </details>

                <!-- ¿Por qué elegir Dapamas? -->
                <details class="seo-item">
                    <summary class="seo-item__header">
                        <h2 class="seo-item__title">
                                ¿Por qué elegir Dapamas entre las tiendas online
                                para envíos a Cuba?
                            </h2>
                    </summary>
                    <div class="seo-item__content">
                        <p>
                            Hay varias opciones cuando buscas cómo enviar
                            productos a la isla. Lo que distingue a Dapamas no
                            es solo el precio (el más competitivo), sino el
                            inventario de productos que ya están en Cuba:
                        </p>
                        <div class="seo-cards seo-cards--three">
                            <article
                                v-for="(item, i) in whyChooseUs"
                                :key="`why-${i}`"
                                class="seo-card"
                            >
                                <h3 class="seo-card__title">
                                    {{ item.title }}
                                </h3>
                                <p>{{ item.body }}</p>
                            </article>
                        </div>
                        <div class="seo-subgroup">
                            <h3 class="seo-subgroup__title">
                                Compras recurrentes para tu familia en Cuba
                            </h3>
                            <p>
                                Muchos clientes de Dapamas hacen sus compras
                                online para Cuba cada mes. Puedes repetir
                                pedidos anteriores, guardar la información de
                                entrega de tu familia y hacer la siguiente
                                compra en mucho menos tiempo.
                            </p>
                            <p>
                                Tanto si necesitas una compra puntual como si
                                buscas una solución regular para apoyar a los
                                tuyos, Dapamas te permite gestionar todo desde
                                un mismo lugar, sin tener que empezar de cero
                                cada vez.
                            </p>
                        </div>
                    </div>
                </details>

                <!-- Preguntas frecuentes -->
                <details class="seo-item">
                    <summary class="seo-item__header">
                        <h2 class="seo-item__title">
                                Preguntas frecuentes
                            </h2>
                    </summary>
                    <div class="seo-item__content">
                        <div class="seo-faq tw-flex tw-flex-col tw-gap-2">
                            <details
                                v-for="(faq, i) in faqs"
                                :key="`faq-${i}`"
                                class="seo-faq__item"
                            >
                                <summary class="seo-faq__header">
                                    <h3 class="seo-faq__title">
                                        {{ faq.q }}
                                    </h3>
                                </summary>
                                <div class="seo-faq__content">
                                    <p>{{ faq.a }}</p>
                                </div>
                            </details>
                        </div>
                    </div>
                </details>
            </div>

            <!-- Empieza hoy tus compras para Cuba online -->
            <div class="seo-cta">
                <h2 class="seo-cta__title">
                    Empieza hoy tus compras para Cuba online
                </h2>
                <p class="seo-cta__body">
                    Si buscas una plataforma confiable para comprar productos en
                    Cuba o ayudar a tu familia desde el exterior, Dapamas te
                    ofrece una forma simple, segura y práctica de hacerlo. Da
                    igual si estás en Madrid, Miami o La Habana. Explora el
                    catálogo y haz tu primera
                    <strong>compra para Cuba</strong>.
                </p>
            </div>
        </div>
    </section>
</template>

<style scoped>
.seo-section {
    @apply tw-border-t tw-border-customGray-200;
}

.seo-item {
    @apply tw-bg-white tw-border tw-border-customGray-200 tw-rounded-md tw-overflow-hidden;
}

.seo-item__header {
    @apply tw-flex tw-items-center tw-justify-between tw-gap-3 tw-cursor-pointer tw-select-none tw-text-primary tw-font-titilium tw-px-4 md:tw-px-6 tw-py-3 md:tw-py-4;
    list-style: none;
}

.seo-item__header::-webkit-details-marker {
    display: none;
}

.seo-item__header::after {
    content: "";
    flex-shrink: 0;
    width: 0.5rem;
    height: 0.5rem;
    border-right: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    transform: rotate(45deg);
    transition: transform 0.2s ease;
}

.seo-item[open] > .seo-item__header::after {
    transform: rotate(-135deg);
}

.seo-item__title {
    @apply tw-font-titilium tw-text-primary tw-font-semibold tw-text-base md:tw-text-lg tw-leading-snug tw-m-0;
}

.seo-item__content {
    @apply tw-flex tw-flex-col tw-gap-4 tw-px-4 md:tw-px-6 tw-py-5 tw-text-customGray-600 tw-font-titilium tw-text-base tw-leading-relaxed;
}

.seo-item__content p {
    @apply tw-m-0;
}

.seo-item__content strong {
    @apply tw-text-primary tw-font-semibold;
}

.seo-cards {
    @apply tw-grid tw-gap-3 tw-mt-1;
}

.seo-cards--two {
    @apply tw-grid-cols-1 md:tw-grid-cols-2;
}

.seo-cards--three {
    @apply tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3;
}

.seo-card {
    @apply tw-bg-customGray-100 tw-rounded-md tw-p-4 tw-flex tw-flex-col tw-gap-2;
}

.seo-card__title {
    @apply tw-font-titilium tw-text-primary tw-font-semibold tw-text-base tw-m-0;
}

.seo-card p {
    @apply tw-m-0 tw-text-customGray-600 tw-text-sm tw-leading-relaxed;
}

.seo-list {
    @apply tw-list-none tw-p-0 tw-m-0 tw-flex tw-flex-col tw-gap-2;
}

.seo-list--steps li {
    @apply tw-flex tw-items-start tw-gap-3 tw-bg-customGray-100 tw-rounded-md tw-p-3 tw-text-customGray-700 tw-text-sm;
}

.seo-list__index {
    @apply tw-shrink-0 tw-w-6 tw-h-6 tw-rounded-full tw-bg-primary tw-text-white tw-text-xs tw-font-bold tw-flex tw-items-center tw-justify-center;
}

.seo-subgroup {
    @apply tw-flex tw-flex-col tw-gap-2 tw-mt-2 tw-pt-4 tw-border-t tw-border-customGray-200;
}

.seo-subgroup__title {
    @apply tw-font-titilium tw-text-primary tw-font-semibold tw-text-base md:tw-text-lg tw-m-0;
}

.seo-subgroup p {
    @apply tw-m-0;
}

.seo-faq__item {
    @apply tw-bg-customGray-100 tw-rounded-md tw-overflow-hidden tw-border tw-border-customGray-200;
}

.seo-faq__header {
    @apply tw-flex tw-items-center tw-justify-between tw-gap-3 tw-cursor-pointer tw-select-none tw-text-primary tw-font-titilium tw-px-3 md:tw-px-4 tw-py-2 md:tw-py-3;
    list-style: none;
}

.seo-faq__header::-webkit-details-marker {
    display: none;
}

.seo-faq__header::after {
    content: "";
    flex-shrink: 0;
    width: 0.45rem;
    height: 0.45rem;
    border-right: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    transform: rotate(45deg);
    transition: transform 0.2s ease;
}

.seo-faq__item[open] > .seo-faq__header::after {
    transform: rotate(-135deg);
}

.seo-faq__title {
    @apply tw-font-titilium tw-text-primary tw-font-semibold tw-text-sm md:tw-text-base tw-m-0 tw-leading-snug;
}

.seo-faq__content {
    @apply tw-px-3 md:tw-px-4 tw-py-3 tw-text-customGray-600 tw-text-sm tw-leading-relaxed;
}

.seo-faq__content p {
    @apply tw-m-0;
}

.seo-cta {
    @apply tw-flex tw-flex-col tw-gap-3 tw-mt-8 md:tw-mt-12 tw-pt-8 tw-border-t tw-border-customGray-200;
}

.seo-cta__title {
    @apply tw-font-titilium tw-text-primary tw-font-bold tw-text-xl md:tw-text-2xl tw-leading-tight tw-m-0;
}

.seo-cta__body {
    @apply tw-font-titilium tw-text-customGray-600 tw-text-base tw-leading-relaxed tw-m-0;
}

.seo-cta__body strong {
    @apply tw-text-primary tw-font-semibold;
}
</style>
